import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { AuthPolicies } from '../../../auth/auth-policies';
import { ShopManagementAdvancedFilterComponent } from '../shop-management-advanced-filter/shop-management-advanced-filter.component';

import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tooltip';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import { WorkOrderSearchTextForm } from '../../../form-model/work-order-search-text.form.model';
import { ToastrService } from '../../../services/toastr-service';
import { TagWithOverflowComponent } from '../../../shared-components/tag-with-overflow/tag-with-overflow.component';
import { SharedDataService } from '../../../services/shared-data.service';
import { ShopManagementFilterForm } from '../../../form-model/shop-management-filter.form.model';
import { ShopManagementFilter } from '../../../models/shop-management-filter.model';
import { getPartSupplierTypeDisplayText, PartSupplierCode } from '../../../form-model/part-supplier-type.model';
import { environment } from '../../../../environments/environment';
import { ShopManagementService } from '../../../services/shop-management.service';
import { DownloadFilterCriteria, toFilter } from '../../../models/download-filter.model';
import { getFilterValue as getSharedFilterValue, removeFilter as removeSharedFilter } from '../../../helper/filter-helpers';
import { downloadBlob, handleDownloadError, generateDateFilename } from '../../../helper/download-helpers';

type FilterValue = string | string[] | boolean[];

@Component({
  selector: 'app-shop-management-filters',
  imports: [FormsModule, ReactiveFormsModule, RouterModule, ShopManagementAdvancedFilterComponent, TagWithOverflowComponent],
  templateUrl: './shop-management-filters.component.html',
  styleUrl: './shop-management-filters.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FiltersComponent implements AfterViewInit {
  showAdvanceFilters: boolean = false;
  isDownload: boolean = false;
  canAddNewShop = this.authPolicies.canAddShopManagement && environment.Features.EnableShopManagementAddEdit;
  readonly screenWidth = window.innerWidth;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    shopManagementFilterForm: FormGroup<ShopManagementFilterForm>;
    workOrderSearchTextForm: FormGroup<WorkOrderSearchTextForm>;
  }>;
  savedFilter!: FormGroup<ShopManagementFilterForm>;
  taggedFilters: { key: string; value: FilterValue }[] = [];
  @Input() isShopManagementList: boolean = true;

  @ViewChild(ShopManagementAdvancedFilterComponent) advanceFilterComponent!: ShopManagementAdvancedFilterComponent;
  constructor(
    public authPolicies: AuthPolicies,
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: ToastrService,
    private readonly sharedDataService: SharedDataService,
    private readonly router: Router,
    private readonly shopService: ShopManagementService,
  ) {}

  ngAfterViewInit(): void {
    this.subscribeToFormChanges();
    this.setFilterFormValue();
  }

  private setFilterFormValue() {
    const filterValue = this.sharedDataService.getShopManagementFilterFormValue();
    if (filterValue) {
      this.filterFormGroup.controls.shopManagementFilterForm.setValue(filterValue);
      this.updateSelectedFiltersSection();
    }
  }
  private subscribeToFormChanges() {
    this.filterFormGroup.controls.shopManagementFilterForm?.controls.applyShopManagementFilter?.valueChanges.subscribe((value) => {
      if (value) {
        this.showAdvanceFilters = false;
        this.updateSelectedFiltersSection();
        this.sharedDataService.setShopManagementFilterFormValue(this.filterFormGroup.controls.shopManagementFilterForm.getRawValue());
      } else {
        this.sharedDataService.setShopManagementFilterFormValue(null);
      }
    });
    this.filterFormGroup.controls.workOrderSearchTextForm.controls.appliedWOSearchTextFilter?.valueChanges.subscribe((_) => {
      this.updateSelectedFiltersSection();
    });
  }

  unselectShopCode(): (_: string) => void {
    return (shopCode: string) => {
      let key = this.taggedFilters.find((x) => x.key == 'Shop Code');
      const shopCodeControl = this.filterFormGroup.controls.shopManagementFilterForm.controls.shopCode;
      const currentValues = key?.value as string[];
      const updatedValue = currentValues.filter((code) => code != shopCode);
      if (key) {
        key.value = updatedValue;
      }
      shopCodeControl.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.shopManagementFilterForm.controls.applyShopManagementFilter.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }
  unselectType(): (_: string) => void {
    return (displayText: string) => {
      let key = this.taggedFilters.find((x) => x.key == 'Type');
      const typeControl = this.filterFormGroup.controls.shopManagementFilterForm.controls.partSupplierType;
      const currentValues = key?.value as PartSupplierCode[];
      const codeToRemove = currentValues.find((code) => getPartSupplierTypeDisplayText(code) === displayText);
      if (!codeToRemove) return; // nothing matches, exit

      const updatedValue = currentValues.filter((code) => code !== codeToRemove);
      if (key) {
        key.value = updatedValue;
      }
      typeControl.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.shopManagementFilterForm.controls.applyShopManagementFilter.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }
  unselectLocation(): (_: string) => void {
    return (location: string) => {
      let key = this.taggedFilters.find((x) => x.key == 'Shop Location');
      const locationControl = this.filterFormGroup.controls.shopManagementFilterForm.controls.locationCode;
      const currentValues = key?.value as string[];
      const updatedValue = currentValues.filter((loc) => loc != location);
      if (key) {
        key.value = updatedValue;
      }
      locationControl.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.shopManagementFilterForm.controls.applyShopManagementFilter.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }
  unselectStatus(status: boolean) {
    const statusControl = this.filterFormGroup.controls.shopManagementFilterForm.controls.isActive;
    const currentValues: boolean[] = statusControl.value || [];

    // Remove the clicked status
    const updatedValues = currentValues.filter((s) => s !== status);

    statusControl.setValue(updatedValues.length ? updatedValues : null);
    this.filterFormGroup.controls.shopManagementFilterForm.controls.applyShopManagementFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  unselectSpecificFilter(taggedFilter: { key: string; value: FilterValue }, itemNumber: number = -1, itemValue = ''): void {
    switch (taggedFilter.key) {
      case 'Shop Code': {
        const shopCodeControl = this.filterFormGroup.controls.shopManagementFilterForm.controls.shopCode;
        const currentShopCodes = taggedFilter.value as string[];
        const updatedShopCodes = currentShopCodes.filter((code) => code != itemValue);
        shopCodeControl.setValue(updatedShopCodes.length ? updatedShopCodes : null);
        break;
      }
      case 'Type': {
        if (Array.isArray(taggedFilter.value) && itemNumber > 0 && itemNumber < (taggedFilter.value as string[]).length) {
          let taggedTypes = taggedFilter.value as string[];
          const updatedTypes: string[] = taggedTypes.filter((type) => type != taggedTypes[itemNumber]);
          this.filterFormGroup.controls.shopManagementFilterForm.controls.partSupplierType.setValue(
            updatedTypes.length ? updatedTypes : null,
          );
        }
        break;
      }
      case 'Shop Location': {
        if (Array.isArray(taggedFilter.value) && itemNumber > 0 && itemNumber < (taggedFilter.value as string[]).length) {
          let taggedLocations = taggedFilter.value as string[];
          const updatedLocations: string[] = taggedLocations.filter((location) => location != taggedLocations[itemNumber]);
          this.filterFormGroup.controls.shopManagementFilterForm.controls.locationCode.setValue(
            updatedLocations.length ? updatedLocations : null,
          );
        }
        break;
      }
      case 'Status': {
        this.filterFormGroup.controls.shopManagementFilterForm.controls.isActive.setValue(null);
        break;
      }
      default:
        break;
    }
    this.filterFormGroup.controls.shopManagementFilterForm.controls.applyShopManagementFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  showAdvanceFiltersModal() {
    this.showAdvanceFilters = true;
    this.saveCurrentFilters();
  }
  onDownload() {
    this.isDownload = true;
    const filters = this.buildDownloadFilters();
    this.shopService.downloadToExcel({ columns: [], filters }).subscribe({
      next: (blob) => {
        downloadBlob(blob, generateDateFilename('shop-management'));
        this.isDownload = false;
      },
      error: (err) => {
        handleDownloadError(err, this.toastrService);
        this.isDownload = false;
      },
    });
  }

  private buildDownloadFilters(): DownloadFilterCriteria[] {
    const filters: DownloadFilterCriteria[] = [];
    const form = this.filterFormGroup.controls.shopManagementFilterForm;

    const shopCodes = form.controls.shopCode?.value;
    if (shopCodes && shopCodes.length > 0) {
      filters.push(toFilter('shop_code', shopCodes));
    }

    const types = form.controls.partSupplierType?.value;
    if (types && types.length > 0) {
      filters.push(toFilter('part_supplier_type', types));
    }

    const locations = form.controls.locationCode?.value;
    if (locations && locations.length > 0) {
      filters.push(toFilter('location_code', locations));
    }

    const statuses = form.controls.isActive?.value;
    if (statuses?.length === 1) {
      filters.push(toFilter('is_active', [statuses[0] ? '1' : '0']));
    }

    return filters;
  }

  closeAdvanceFiltersModal() {
    if (this.showAdvanceFilters) {
      this.showAdvanceFilters = false;
      this.filterFormGroup.controls.shopManagementFilterForm.reset(this.savedFilter.value);
    }
    this.updateSelectedFiltersSection();
  }

  clearAllSelectedFilters() {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.shopManagementFilterForm.controls.applyShopManagementFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  private saveCurrentFilters(): void {
    this.savedFilter = this.formBuilder.group({
      shopCode: this.formBuilder.control<string[] | null>(this.filterFormGroup.controls.shopManagementFilterForm.controls.shopCode.value),
      partSupplierType: this.formBuilder.control<string[] | null>(
        this.filterFormGroup.controls.shopManagementFilterForm.controls.partSupplierType.value,
      ),
      locationCode: this.formBuilder.control<string[] | null>(
        this.filterFormGroup.controls.shopManagementFilterForm.controls.locationCode.value,
      ),
      isActive: this.formBuilder.control<boolean[] | null>(this.filterFormGroup.controls.shopManagementFilterForm.controls.isActive.value),
      applyShopManagementFilter: this.formBuilder.control<boolean | null>(
        this.filterFormGroup.controls.shopManagementFilterForm.controls.applyShopManagementFilter.value,
      ),
    });
  }

  updateSelectedFiltersSection() {
    this.markAsTagged('Shop Code', this.filterFormGroup.controls.shopManagementFilterForm.controls.shopCode.value);
    this.markAsTagged('Type', this.filterFormGroup.controls.shopManagementFilterForm.controls.partSupplierType.value);
    this.markAsTagged('Shop Location', this.filterFormGroup.controls.shopManagementFilterForm.controls.locationCode.value);
    this.markAsTagged('Status', this.filterFormGroup.controls.shopManagementFilterForm.controls.isActive.value);
    this.updateStatusTag();
  }

  private updateStatusTag() {
    const filters: ShopManagementFilter = {};
    if (
      this.filterFormGroup.controls.shopManagementFilterForm.controls.shopCode.value &&
      this.filterFormGroup.controls.shopManagementFilterForm.controls.shopCode.value.length > 0
    ) {
      filters.shopCodesList = this.filterFormGroup.controls.shopManagementFilterForm.controls.shopCode.value;
    }
    if (
      this.filterFormGroup.controls.shopManagementFilterForm.controls.partSupplierType.value &&
      this.filterFormGroup.controls.shopManagementFilterForm.controls.partSupplierType.value.length > 0
    ) {
      filters.partSupplierTypesList = this.filterFormGroup.controls.shopManagementFilterForm.controls.partSupplierType.value;
    }
    if (
      this.filterFormGroup.controls.shopManagementFilterForm.controls.locationCode.value &&
      this.filterFormGroup.controls.shopManagementFilterForm.controls.locationCode.value.length > 0
    ) {
      filters.locationCodeList = this.filterFormGroup.controls.shopManagementFilterForm.controls.locationCode.value;
    }
    if (
      this.filterFormGroup.controls.shopManagementFilterForm.controls.isActive.value &&
      this.filterFormGroup.controls.shopManagementFilterForm.controls.isActive.value.length > 0
    ) {
      filters.shopActiveStatusesList = this.filterFormGroup.controls.shopManagementFilterForm.controls.isActive.value;
    }
  }

  markAsTagged(key: string, newValue: FilterValue | null) {
    const currentValue = this.getFilterValue(key);
    if (currentValue !== newValue) {
      this.removeFilter(key);
      if (newValue !== null && newValue !== '' && (!Array.isArray(newValue) || newValue.length > 0)) {
        this.taggedFilters.unshift({ key, value: newValue });
      }
    }
  }

  removeFilter(key: string) {
    removeSharedFilter(this.taggedFilters, key);
  }

  getFilterValue(key: string) {
    return getSharedFilterValue(this.taggedFilters, key);
  }

  getTaggedFilterValue(taggedFilter: { key: string; value: FilterValue }, itemNumber: number = -1): FilterValue {
    switch (taggedFilter.key) {
      case 'Shop Code': {
        let shopCodes = taggedFilter.value as string[];
        return itemNumber >= 0 && itemNumber < shopCodes.length ? shopCodes[itemNumber] : shopCodes;
      }
      case 'Type': {
        let types = taggedFilter.value as string[];
        return itemNumber >= 0 && itemNumber < types.length ? types[itemNumber] : types;
      }
      case 'Shop Location': {
        let locations = taggedFilter.value as string[];
        return itemNumber >= 0 && itemNumber < locations.length ? locations[itemNumber] : locations;
      }
      case 'Status': {
        let statuses = taggedFilter.value as boolean[];
        return statuses;
      }
      default:
        return '';
    }
  }

  getTaggedShopCodes(): string[] {
    const shopCodeFilter = this.taggedFilters.find((filter) => filter.key === 'Shop Code');
    return shopCodeFilter && this.isArrayType(shopCodeFilter.value) ? (shopCodeFilter.value as string[]) : [];
  }
  getTaggedTypes(): string[] {
    const typeFilter = this.taggedFilters.find((filter) => filter.key === 'Type');
    if (typeFilter && this.isArrayType(typeFilter.value)) {
      const types = typeFilter.value as PartSupplierCode[]; // <-- raw codes
      // Map to display text for UI only
      return types.map((code) => getPartSupplierTypeDisplayText(code));
    }
    return [];
  }

  getTaggedLocations(): string[] {
    const locationFilter = this.taggedFilters.find((filter) => filter.key === 'Shop Location');
    return locationFilter && this.isArrayType(locationFilter.value) ? (locationFilter.value as string[]) : [];
  }
  getTaggedStatuses(): boolean[] {
    const statusFilter = this.taggedFilters.find((filter) => filter.key === 'Status');
    return statusFilter && this.isArrayType(statusFilter.value) ? (statusFilter.value as boolean[]) : [];
  }

  isArrayType(value: FilterValue): boolean {
    return Array.isArray(value);
  }

  onAddNewShopClicked() {
    this.router.navigate(['/master-data/shop-management/add-new']);
  }
}
